import React from 'react';

/**
 * Dependency-free SVG radar chart for skill performance (ROO-131).
 * Needs >= 3 axes; the caller falls back to domain axes otherwise.
 */
export interface RadarAxis {
  label: string;
  percent: number;
}

const COLOR = '#2f8f5b';
const clampPct = (p: number) => Math.max(0, Math.min(100, Math.round(p)));

// Wrap a label into at most two lines on a word boundary (ROO-216) — long
// skill names used to be hard-truncated AND clipped by the viewBox edge.
const wrapLabel = (s: string): string[] => {
  if (s.length <= 14) return [s];
  const words = s.split(/\s+/);
  let first = '';
  while (words.length && (first + ' ' + words[0]).trim().length <= 14) {
    first = (first + ' ' + words.shift()).trim();
  }
  if (!first) first = words.shift() || '';
  const rest = words.join(' ');
  const second = rest.length > 14 ? rest.slice(0, 13) + '…' : rest;
  return second ? [first, second] : [first];
};

export default function RadarChart({
  axes,
  size = 340,
}: {
  axes: RadarAxis[];
  size?: number;
}) {
  if (!axes || axes.length < 3) return null;

  const n = axes.length;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 64; // leave room for labels
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const point = (i: number, radius: number): [number, number] => [
    cx + radius * Math.cos(angle(i)),
    cy + radius * Math.sin(angle(i)),
  ];
  const poly = (pts: [number, number][]) => pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  const rings = [0.25, 0.5, 0.75, 1];
  const dataPts = axes.map((a, i) => point(i, (r * clampPct(a.percent)) / 100));

  // Horizontal padding so side labels render fully instead of being clipped
  // at the viewBox edge (ROO-216).
  const PAD = 78;

  return (
    <svg
      viewBox={`${-PAD} 0 ${size + PAD * 2} ${size}`}
      width="100%"
      style={{ maxWidth: size + PAD * 2, display: 'block', margin: '0 auto', overflow: 'visible' }}
      role="img"
      aria-label="Skill performance radar chart"
    >
      {rings.map((ring, ri) => (
        <polygon
          key={ri}
          points={poly(axes.map((_, i) => point(i, r * ring)))}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={1}
        />
      ))}
      {axes.map((_, i) => {
        const [x, y] = point(i, r);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#e5e7eb" strokeWidth={1} />;
      })}
      <polygon points={poly(dataPts)} fill="rgba(47,143,91,0.20)" stroke={COLOR} strokeWidth={2} />
      {dataPts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={3.5} fill={COLOR} />
      ))}
      {axes.map((a, i) => {
        const [x, y] = point(i, r + 20);
        const c = Math.cos(angle(i));
        const anchor = Math.abs(c) < 0.3 ? 'middle' : c > 0 ? 'start' : 'end';
        const lines = wrapLabel(a.label);
        return (
          <text
            key={i}
            x={x}
            y={lines.length > 1 ? y - 6 : y}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontSize={11}
            fill="#374151"
          >
            {lines.map((line, li) => (
              <tspan key={li} x={x} dy={li === 0 ? 0 : 13}>
                {line}
                {li === lines.length - 1 && (
                  <tspan fill="#9ca3af"> {clampPct(a.percent)}%</tspan>
                )}
              </tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
}
